import { observer, useLocalObservable } from 'mobx-react';
import { useStore } from '../../../../store/store';
import IconButton, { ICON_BUTTON_ICONS } from '../../../../components/buttons/IconButton';
import GeneralLoader from '../../../../components/loaders/GeneralLoader';
import { Fragment, useEffect } from 'react';
import { Oval } from 'react-loader-spinner';
import { runInAction } from 'mobx';
import { formatNumber } from '../../../../utils/utils';
import XRegExp from 'xregexp';
import { PUNCTUATION } from '../../../../utils/constants/notation';
import { getLogoUrl, LOGO_PLACEHOLDER } from '../../../../utils/logo';
import pubSub from '../../../../utils/pubsub';
import { Tooltip } from 'react-tooltip';

const highlightSearchKey = (name, searchKey) => {
  if (!searchKey) return name;

  // Normalize the search key
  const normalizedSearchKey = XRegExp.escape(
    searchKey
      .trim()
      .toLowerCase()
      .normalize('NFD')
      .replace(/\p{Diacritic}/gu, '')
  );

  // Create a regex pattern with the normalized search key
  const regex = XRegExp(`(${normalizedSearchKey})`, 'gi');

  // Normalize the name
  const normalizedName = name
    .trim()
    .toLowerCase()
    .normalize('NFD')
    .replace(/\p{Diacritic}/gu, '');

  // Split the name using the regex pattern
  const parts = XRegExp.split(normalizedName, regex);

  let currentIndex = 0;
  return parts.map((part, index) => {
    if (XRegExp.test(part, regex)) {
      // If the part matches the search key, highlight it
      const originalPart = name.slice(currentIndex, currentIndex + part.length);
      currentIndex += part.length;
      return (
        <span key={index} className="highlight-option-name">
          {originalPart}
        </span>
      );
    } else {
      // Otherwise, return the original part
      const originalPart = name.slice(currentIndex, currentIndex + part.length);
      currentIndex += part.length;
      return originalPart;
    }
  });
};

const ProfilerCompanyNameSearch = observer(
  ({
    isTyping,
    searchKey,
    setSearchKey,
    clearSearchKey,
    searchKeyResults,
    isLoading,
    loadError,
    isLoadingCompanyData,
    loadCompanyData
  }) => {
    const { utilsStore, profilerAccessStore, authStore } = useStore();
    const state = useLocalObservable(() => ({
      showLoadingIndicator: {},
      setShowLoadingIndicator: (companyId = '', show = false) => {
        state.showLoadingIndicator[companyId] = show;
      },
      toggledLinkContextMenu: {},
      toggleLinkContextMenu: (companyId) => {
        if (companyId) {
          state.toggledLinkContextMenu[companyId] = !state.toggledLinkContextMenu[companyId];
        } else {
          state.toggledLinkContextMenu = {};
        }
      }
    }));

    useEffect(() => {
      if (!isLoading) {
        runInAction(() => {
          state.showLoadingIndicator = {};
        });
      }
    }, [state, isLoading]);

    useEffect(() => {
      const onAnyClick = () => {
        state.toggleLinkContextMenu();
      };

      const onPubSubContextClick = (payload) => {
        if (payload?.event === 'icon-button-context-menu-trigger') {
          state.toggleLinkContextMenu();
        }
      };

      pubSub.subscribe(onPubSubContextClick);
      document.addEventListener('click', onAnyClick);

      return () => {
        pubSub.unsubscribe(onPubSubContextClick);
        document.removeEventListener('click', onAnyClick);
      };
    }, [state]);

    const disableSearchForm =
      (profilerAccessStore.isProfilerDemo && profilerAccessStore.demoSearchExceededLimit) ||
      isLoadingCompanyData;
    const showNoResults =
      !isTyping && !isLoading && !loadError && !searchKeyResults.length && searchKey.length >= 3;
    const showLoader = !isTyping && isLoading;
    const showError = !isTyping && !!loadError;
    const showResults = !isTyping && !!searchKeyResults.length;

    return (
      <div className="profiler-search-by-name-wrapper">
        <div className="search-key-input-wrapper">
          <div className="search-key-label">Company name and legal form</div>
          <div className="search-key-input-wrap">
            <input
              id="ps-input-searchKey"
              type="text"
              value={searchKey}
              onChange={(e) => setSearchKey(e.target.value)}
              placeholder="Type company name here"
              disabled={disableSearchForm}
              onKeyDown={(e) => {
                if (e.key !== 'ArrowDown' && e.key !== 'ArrowUp') return;

                const container = document.querySelector('.profiler-name-search-results');
                if (!container) return;

                const entries = container.querySelectorAll('.profiler-name-search-result-entry');
                const count = entries.length;
                if (count === 0) return;

                let target = null;
                if (count === 1) {
                  target = entries[0];
                } else {
                  target = e.key === 'ArrowDown' ? entries[0] : entries[count - 1];
                }

                if (target) {
                  e.preventDefault();
                  e.stopPropagation();
                  target.focus();
                }
              }}
            />
            {!searchKey && <img className="search-icon" src={ICON_BUTTON_ICONS.SEARCH} />}
            {!!searchKey && (
              <IconButton
                id="profiler-name-search-clear-icon"
                icon={ICON_BUTTON_ICONS.X_LG}
                withFill={false}
                withBorder={false}
                onClick={clearSearchKey}
                disabled={disableSearchForm}
              />
            )}
          </div>
        </div>
        {showNoResults && <div className="no-profiler-name-results">No results found</div>}
        {showLoader && <GeneralLoader />}
        {showError && <div className="error">{loadError}</div>}
        {showResults && (
          <div className="profiler-name-search-results">
            {searchKeyResults.map((company) => {
              const logoUrl = getLogoUrl(company.website_SAFE_LOGO);
              return (
                <Fragment key={company.id}>
                  <div
                    id={`profiler-name-search-result-entry-${company.id}`}
                    className={`profiler-name-search-result-entry ${
                      isLoadingCompanyData ? 'no-hover' : ''
                    } ${state.showLoadingIndicator[company.id] ? 'active' : ''}`}
                    onContextMenu={(event) => {
                      if (isLoadingCompanyData) {
                        return;
                      }

                      event.preventDefault();
                      pubSub.publish({ event: 'icon-button-context-menu-trigger' });
                      state.toggleLinkContextMenu(company.id);
                    }}
                    onClick={() => {
                      if (!isLoadingCompanyData) {
                        state.setShowLoadingIndicator(company.id, true);
                        loadCompanyData(company.id, () => {
                          state.setShowLoadingIndicator(company.id, false);
                        });
                      }
                    }}
                    tabIndex={0}
                    onKeyDown={(event) => {
                      const isEnter = event.key === 'Enter';
                      const isDown = event.key === 'ArrowDown';
                      const isUp = event.key === 'ArrowUp';

                      if (isEnter) {
                        if (!isLoadingCompanyData) {
                          state.setShowLoadingIndicator(company.id, true);
                          loadCompanyData(company.id, () => {
                            state.setShowLoadingIndicator(company.id, false);
                          });
                        }
                        return;
                      }

                      if (!isDown && !isUp) return;

                      const entries = document.querySelectorAll(
                        '.profiler-name-search-result-entry'
                      );
                      const count = entries.length;
                      if (count === 0) return;

                      const currentIndex = Array.from(entries).indexOf(event.currentTarget);
                      if (currentIndex === -1) return;

                      let target = null;

                      if (isDown) {
                        if (currentIndex === count - 1) {
                          target = document.getElementById('ps-input-searchKey');
                        } else {
                          target = entries[currentIndex + 1];
                        }
                      } else if (isUp) {
                        if (currentIndex === 0) {
                          target = document.getElementById('ps-input-searchKey');
                        } else {
                          target = entries[currentIndex - 1];
                        }
                      }

                      if (target) {
                        event.preventDefault();
                        event.stopPropagation();
                        target.focus();
                      }
                    }}
                  >
                    <div className="info-wrap">
                      {utilsStore.windowWidth >= 834 && (
                        <div className="website-logo-wrap">
                          <img
                            className="website-logo"
                            src={logoUrl || LOGO_PLACEHOLDER}
                          />
                        </div>
                      )}
                      <div className="inner-info-wrap">
                        <div className="company-name-wrap">
                          {utilsStore.windowWidth < 834 && (
                            <div className="website-logo-wrap-mobile">
                              <img
                                className="website-logo-mobile"
                                src={logoUrl || LOGO_PLACEHOLDER}
                              />
                            </div>
                          )}
                          <div className="company-name">
                            {highlightSearchKey(company.name, searchKey)}
                          </div>
                        </div>
                        {/* <div className="company-name">
                        {company.name
                          .toLowerCase()
                          .normalize('NFD')
                          .replace(/\p{Diacritic}/gu, '')}
                      </div> */}
                        <div className="info-row">
                          <div className="info-row-item" title="Company headquarters">
                            <div className="icon">
                              <img src={ICON_BUTTON_ICONS.GEO_ALT} />
                            </div>
                            <div className="content">{company.headquarters}</div>
                          </div>
                          <div className="item-separator" />
                          <div
                            className="info-row-item"
                            title={`Company sales (in millions of euros)${
                              company.revenueYear ? ` in ${company.revenueYear}` : ''
                            }`}
                          >
                            <div className="icon">
                              <img src={ICON_BUTTON_ICONS.CASH_STACK} />
                            </div>
                            <div className="content">
                              {!!company.revenue && (
                                <>
                                  {company.revenue}
                                  <span className="year">
                                    &lsquo;
                                    {company.revenueYear?.toString?.()?.slice?.(-2) ||
                                      PUNCTUATION.EMPTY_VALUE}
                                  </span>
                                  &nbsp;€M
                                </>
                              )}
                              {!company.revenue && <>{PUNCTUATION.EMPTY_VALUE}</>}
                            </div>
                          </div>
                          <div className="item-separator" />
                          <div
                            className="info-row-item"
                            title={`Company employees${
                              company.employeesYear ? ` in ${company.employeesYear}` : ''
                            }`}
                          >
                            <div className="icon">
                              <img src={ICON_BUTTON_ICONS.PEOPLE} />
                            </div>
                            <div className="content">
                              {!isNaN(company.employees) && company.employees !== null ? (
                                <>
                                  {formatNumber((+company.employees).toFixed(2))}
                                  <span className="year">
                                    &lsquo;
                                    {company.employeesYear?.toString?.()?.slice?.(-2) ||
                                      PUNCTUATION.EMPTY_VALUE}
                                  </span>
                                </>
                              ) : (
                                PUNCTUATION.EMPTY_VALUE
                              )}
                            </div>
                          </div>
                          {/* <div className="item-separator" /> */}
                          <div className="info-row-item industries">
                            {utilsStore.windowWidth < 834 ? (
                              <>
                                <div className="icon">
                                  <img src={ICON_BUTTON_ICONS.BUILDING} />
                                </div>
                                <div className="content industries">
                                  {!!company.industries.length && (
                                    <>{company.industries.join(', ')}</>
                                  )}
                                  {!company.industries.length && <>{PUNCTUATION.EMPTY_VALUE}</>}
                                </div>
                              </>
                            ) : (
                              <div className="desktop-industries-wrap" title="Company industries">
                                <div className="icon">
                                  <img src={ICON_BUTTON_ICONS.BUILDING} />
                                </div>
                                <div className="desktop-industries-text">
                                  {!!company.industries.length && (
                                    <>{company.industries.join(', ')}</>
                                  )}
                                  {!company.industries.length && <>{PUNCTUATION.EMPTY_VALUE}</>}
                                </div>
                              </div>
                            )}
                          </div>
                          {/* <div className="item-separator" />
                        <div className="info-row-item website">
                          <div className="icon">
                            <img src={ICON_BUTTON_ICONS.GLOBE2} />
                          </div>
                          <div className="content">
                            {!!company.website && (
                              <div
                                className="website-link"
                                onClick={() => {
                                  window.open('https://' + company.website_SAFE, '_blank');
                                }}>
                                {company.website}
                              </div>
                            )}
                            {!company.website && <>{PUNCTUATION.EMPTY_VALUE}</>}
                          </div>
                        </div> */}
                        </div>
                      </div>
                    </div>

                    {state.showLoadingIndicator[company.id] && (
                      <Oval
                        visible={true}
                        height="20"
                        width="20"
                        color="#348CF9"
                        secondaryColor="#D6D6D6"
                        strokeWidth={5}
                        ariaLabel="oval-loading"
                        wrapperStyle={{}}
                        wrapperClass="oval-loading"
                      />
                    )}
                  </div>
                  {utilsStore.windowWidth < 834 && <div className="company-entry-separator" />}
                  {state.toggledLinkContextMenu[company.id] && (
                    <Tooltip
                      anchorSelect={`#profiler-name-search-result-entry-${company.id}`}
                      multiline={true}
                      variant="dark"
                      opacity={1}
                      place="bottom-end"
                      clickable={true}
                      isOpen={true}
                      className="icon-button-tooltip-context-link-menu"
                    >
                      <div
                        className="context-link-menu-item"
                        onClick={() => {
                          utilsStore.openCompanyDetails(company.id, {
                            ref: 'search results',
                            searchKey: searchKey || undefined
                          });
                        }}
                      >
                        <div className="left-side">
                          <div className="context-link-icon-wrap">
                            <img src={ICON_BUTTON_ICONS.BOX_ARROW_UP_RIGHT} />
                          </div>
                          <div className="context-link-text">Open in new tab</div>
                        </div>
                        <div className="right-side"></div>
                      </div>
                    </Tooltip>
                  )}
                </Fragment>
              );
            })}
          </div>
        )}
      </div>
    );
  }
);

export default ProfilerCompanyNameSearch;
